import React, { FC, useState } from 'react';
import cx from 'classnames';
import { useHistory } from 'react-router-dom';
import {
  Button as AntButton,
  Card as AntCard,
  Select as AntdSelect,
} from 'antd';

import LoadingContainer from '../LoadingContainer/LoadingContainer';
import compareByDemoAccess from '../../helpers/compare-by-demo-access';
import { ITariff, ITariffOptions, IThemes } from '../../interfaces/common';
import { APP_URL, DATA } from '../../constants/common-app.const';

import css from './KnowledgeCards.module.css';

interface IProps {
  themes: IThemes[];
  tariffs: ITariff[];
  tariff: ITariffOptions;
  isLoading: boolean;
  setTariff: Function;
  setPaymentData: Function;
  togglePaymentModal: Function;
}

const KnowledgeCards: FC<IProps> = ({
  themes,
  tariff,
  tariffs,
  setTariff,
  isLoading,
  setPaymentData,
  togglePaymentModal,
}) => {
  const history = useHistory();
  const [selectedTariff, setSelectedTariff] = useState<{
    [key: string]: string;
  }>({});

  return (
    <div className={css.SpecWrapper}>
      <LoadingContainer isLoading={isLoading}>
        {themes?.length && tariffs?.length ? (
          themes.sort(compareByDemoAccess).map((theme) => {
            const activeSubscriptions =
              new Date(
                theme.userSubscriptions[
                  theme.userSubscriptions.length - 1
                ]?.expireAt,
              ) > new Date();

            const activeInFutureSubscriptions =
              new Date(
                theme.userSubscriptions[
                  theme.userSubscriptions.length - 1
                ]?.startAt,
              ) > new Date();

            const expireAt =
              theme.userSubscriptions[theme.userSubscriptions.length - 1]
                ?.expireAt;

            const startAt =
              theme.userSubscriptions[theme.userSubscriptions.length - 1]
                ?.startAt;

            const isSubscriptionExpired = new Date(expireAt) < new Date();

            const prepareDate = (date: string) =>
              new Date(date).toLocaleString('ru', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              });

            const expireDate = expireAt ? prepareDate(expireAt) : null;

            const startDate = startAt ? prepareDate(startAt) : null;

            const getDaysNaming = (daysAmount: number): string => {
              if (daysAmount % 10 === 1 && daysAmount % 100 !== 11) {
                return `${daysAmount} день`;
              }
              if (
                [2, 3, 4].includes(daysAmount % 10) &&
                ![12, 13, 14].includes(daysAmount % 100)
              ) {
                return `${daysAmount} дня`;
              }
              return `${daysAmount} дней`;
            };

            const getAmountNaming = (amount: number): string => {
              const hasFractionalPart = amount % 1 !== 0;

              if (hasFractionalPart) {
                return `${amount} рубля`;
              }

              const lastDigit = amount % 10;
              const lastTwoDigits = amount % 100;

              if (lastDigit === 1 && lastTwoDigits !== 11) {
                return `${amount} рубль`;
              }
              if (
                [2, 3, 4].includes(lastDigit) &&
                ![12, 13, 14].includes(lastTwoDigits)
              ) {
                return `${amount} рубля`;
              }
              return `${amount} рублей`;
            };

            const currentTariffs: Array<{
              label: React.ReactNode;
              value: string;
            }> = (theme?.tariffs?.length ? theme?.tariffs : tariffs)
              .sort((a, b) => (a.cost > b.cost ? 1 : -1))
              .map(({ title, id, cost, duration, originalCost }) => {
                let label: React.JSX.Element = <span>{title}</span>;
                if (originalCost) {
                  label = (
                    <span>
                      {getDaysNaming(duration)} /{' '}
                      <span
                        style={{
                          textDecoration: 'line-through',
                          color: '#999',
                          opacity: 0.7,
                        }}
                      >
                        {originalCost}
                      </span>{' '}
                      {getAmountNaming(cost)}
                    </span>
                  );
                }
                return { label, value: id };
              });

            return (
              <AntCard className={css.SpecContainer} key={theme.id}>
                <p className={css.SpecName}>
                  {theme?.demoAccessEnabled &&
                    (!expireAt || isSubscriptionExpired) && (
                      <span className={css.DemoFlag}>ДЕМОВЕРСИЯ</span>
                    )}
                  {theme.title}
                </p>

                <div className={css.RateSelectWrapper}>
                  <AntdSelect
                    defaultValue={'default'}
                    className={css.RateSelect}
                    onChange={(value) => {
                      setTariff(
                        (state: ITariffOptions) =>
                          ({
                            ...state,
                            [theme.id]: currentTariffs.find(
                              (tariff: {
                                label: React.ReactNode;
                                value: string;
                              }) => tariff.value === value,
                            ),
                          } as ITariffOptions),
                      );
                      setSelectedTariff((prevState) => ({
                        ...prevState,
                        [theme.id]: value,
                      }));
                    }}
                  >
                    <AntdSelect.Option value={'default'} key={'default'}>
                      Выберите тариф
                    </AntdSelect.Option>
                    {currentTariffs.map((tariff) => (
                      <AntdSelect.Option
                        value={tariff.value}
                        key={tariff.value}
                      >
                        {tariff.label}
                      </AntdSelect.Option>
                    ))}
                  </AntdSelect>
                </div>

                <p className={css.ChooseRate}>
                  {activeInFutureSubscriptions && startAt
                    ? // Если у пользователя активна подписка и есть дата начала подписки
                      `Подписка начнёт действовать: ${startDate}`
                    : activeSubscriptions
                    ? // Если подписка пользователя активна и есть дата окончания подписки
                      `Ваша подписка истекает: ${expireDate}`
                    : theme.userSubscriptions.length &&
                      theme.userSubscriptions[
                        theme.userSubscriptions.length - 1
                      ]?.expireAt !== null
                    ? // Если у пользователя есть история подписок и последняя подписка истекла
                      `Ваша подписка истекла: ${expireDate}`
                    : null}
                </p>

                <AntButton
                  className={css.BuyBtn}
                  onClick={() => {
                    setPaymentData([theme.id, tariff[theme.id]?.value]);
                    togglePaymentModal();
                  }}
                  disabled={
                    tariff[theme.id]?.value !== selectedTariff[theme.id]
                  }
                >
                  {!theme.userSubscriptions.length ||
                  theme.userSubscriptions[theme.userSubscriptions.length - 1]
                    ?.expireAt === null
                    ? 'Купить'
                    : 'Продлить'}
                </AntButton>

                <AntButton
                  className={cx(
                    css.ButtonWrapper,
                    activeSubscriptions && css.ViewBtn,
                  )}
                  onClick={() => history.push(`${APP_URL.THEMES}/${theme.id}`)}
                >
                  {activeSubscriptions ? 'Начать' : 'Просмотр'}
                </AntButton>
              </AntCard>
            );
          })
        ) : (
          <div className={css.NoData}>{DATA.NOT_FOUND}</div>
        )}
      </LoadingContainer>
    </div>
  );
};

export default KnowledgeCards;
